import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PremiumHeroAction {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: ReactNode;
}

interface PremiumHeroProps {
  title: string;
  subtitle?: string;
  badge?: string;
  backgroundImage?: string;
  className?: string;
  children?: ReactNode;
  showScrollIndicator?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  overlay?: "dark" | "gradient" | "light";
  align?: "left" | "center";
  primaryAction?: PremiumHeroAction;
  secondaryAction?: PremiumHeroAction;
}

const PremiumHero = ({
  title,
  subtitle,
  badge,
  backgroundImage,
  className,
  children,
  showScrollIndicator = false,
  size = "md",
  overlay = "gradient",
  align = "left",
  primaryAction,
  secondaryAction,
}: PremiumHeroProps) => {
  const sizeClasses = {
    sm: "py-12 md:py-16",
    md: "py-16 md:py-24",
    lg: "py-20 md:py-32",
    xl: "py-24 md:py-40",
  };

  const overlayClasses = {
    dark: "bg-primary/90",
    gradient: "bg-gradient-to-br from-primary/95 via-primary/85 to-primary/75",
    light: "bg-primary/70",
  };

  return (
    <section className={cn("relative overflow-hidden", className)}>
      {/* Background Image */}
      {backgroundImage && (
        <div className="absolute inset-0">
          <img
            src={backgroundImage}
            alt=""
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="w-full h-full object-cover"
          />
          <div className={cn("absolute inset-0", overlayClasses[overlay])} />
        </div>
      )}

      {/* Default dark background if no image */}
      {!backgroundImage && (
        <div className="absolute inset-0 bg-hero-premium" />
      )}

      {/* Brand motif overlay */}
      <div className="absolute inset-0 hero-brand-motif pointer-events-none" />

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/4 -left-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div
        className={cn(
          "container relative",
          sizeClasses[size],
          align === "center" && "text-center"
        )}
      >
        <div className={cn("max-w-4xl", align === "center" && "mx-auto")}>
          {/* Badge */}
          {badge && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/20 backdrop-blur-sm border border-secondary/30 mb-6 animate-fade-in">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span>
              </span>
              <span className="text-sm font-medium text-primary-foreground/90 tracking-wide">
                {badge}
              </span>
            </div>
          )}

          {/* Title */}
          <h1 
            className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-4 md:mb-6 animate-slide-up text-shadow-hero"
            style={{ animationDelay: "0.1s" }}
          >
            {title}
          </h1>

          {/* Subtitle */}
          {subtitle && (
            <p 
              className={cn(
                "text-lg md:text-xl lg:text-2xl text-primary-foreground/80 max-w-2xl animate-slide-up",
                align === "center" && "mx-auto"
              )}
              style={{ animationDelay: "0.2s" }}
            >
              {subtitle}
            </p>
          )}

          {/* Actions */}
          {(primaryAction || secondaryAction) && (
            <div 
              className={cn(
                "mt-8 flex flex-wrap gap-4 animate-slide-up",
                align === "center" && "justify-center"
              )}
              style={{ animationDelay: "0.3s" }}
            >
              {primaryAction && (
                <Button
                  size="lg"
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-lg gap-2"
                  onClick={primaryAction.onClick}
                  asChild={!!primaryAction.href}
                >
                  {primaryAction.href ? (
                    <a href={primaryAction.href}>
                      {primaryAction.icon}
                      {primaryAction.label}
                    </a>
                  ) : (
                    <>
                      {primaryAction.icon}
                      {primaryAction.label}
                    </>
                  )}
                </Button>
              )}
              {secondaryAction && (
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 gap-2"
                  onClick={secondaryAction.onClick}
                  asChild={!!secondaryAction.href}
                >
                  {secondaryAction.href ? (
                    <a href={secondaryAction.href}>
                      {secondaryAction.icon}
                      {secondaryAction.label}
                    </a>
                  ) : (
                    <>
                      {secondaryAction.icon}
                      {secondaryAction.label}
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Additional Content */}
          {children && (
            <div 
              className="mt-8 animate-slide-up"
              style={{ animationDelay: "0.3s" }}
            >
              {children}
            </div>
          )}
        </div>
      </div>

      {/* Scroll Indicator */}
      {showScrollIndicator && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-scroll-hint">
          <ChevronDown className="h-6 w-6 text-primary-foreground/60" />
        </div>
      )}

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default PremiumHero;
